import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Mail } from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { envConfig } from '../config/authConfig';

const API_BASE_URL = envConfig.apiBaseUrl;

interface UserSuggestion {
  employeeId: string;
  displayName: string;
  email: string;
  jobTitle?: string;
}

interface RecipientPickerProps {
  /** Current list of recipient emails as chips. */
  value: string[];
  /** Called whenever the recipient list changes. */
  onChange: (next: string[]) => void;
  /** Visible label above the input. */
  label?: string;
  /** Placeholder for the input field. */
  placeholder?: string;
  /** Disable all interaction (e.g. while sending). */
  disabled?: boolean;
}

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/**
 * Outlook-compose-style recipient picker.
 *
 *  - Type a name → Graph User search autocomplete dropdown appears
 *  - Click a suggestion (or press Enter) → adds the email as a chip
 *  - Type a raw email + comma / semicolon / Enter → adds it as a chip
 *  - Backspace in an empty input → removes last chip
 *  - X on each chip removes that recipient
 */
export const RecipientPicker: React.FC<RecipientPickerProps> = ({
  value,
  onChange,
  label = 'To',
  placeholder = 'Add a name or email…',
  disabled = false
}) => {
  const { apiCall } = useApiClient();

  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search against Graph
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    const q = input.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSearching(false);
      return;
    }

    debounceRef.current = window.setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await apiCall(
          `${API_BASE_URL}/api/UserSearch/search?q=${encodeURIComponent(q)}`
        );
        if (resp.ok) {
          const json = await resp.json();
          setSuggestions(Array.isArray(json.data) ? json.data : []);
          setHighlightIndex(-1);
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [input, apiCall]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const addEmail = useCallback(
    (raw: string): boolean => {
      const email = raw.trim().toLowerCase();
      if (!email) return false;
      if (!EMAIL_REGEX.test(email)) return false;
      if (value.some((v) => v.toLowerCase() === email)) {
        setInput('');
        return true; // silently dedupe
      }
      onChange([...value, email]);
      setInput('');
      setSuggestions([]);
      setHighlightIndex(-1);
      return true;
    },
    [value, onChange]
  );

  const removeAt = useCallback(
    (idx: number) => {
      const next = [...value];
      next.splice(idx, 1);
      onChange(next);
    },
    [value, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Navigate suggestion list
    if (showDropdown && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' && highlightIndex >= 0) {
        e.preventDefault();
        const picked = suggestions[highlightIndex];
        if (picked?.email) addEmail(picked.email);
        return;
      }
    }

    // Commit typed email on Enter / comma / semicolon / Tab
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      if (input.trim()) {
        e.preventDefault();
        addEmail(input);
      }
    }

    // Backspace on empty input → remove last chip
    if (e.key === 'Backspace' && !input && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  return (
    <div ref={containerRef} className="w-full">
      {label && (
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          {label}
          {value.length > 0 && (
            <span className="ml-2 text-xs font-medium text-slate-400">
              ({value.length} {value.length === 1 ? 'recipient' : 'recipients'})
            </span>
          )}
        </label>
      )}

      <div
        className={`min-h-[44px] w-full flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-xl border bg-white transition-all
          ${disabled
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
            : 'border-slate-300 focus-within:border-pink-400 focus-within:ring-2 focus-within:ring-pink-500/20'}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((email, idx) => (
          <span
            key={`${email}-${idx}`}
            className="inline-flex items-center gap-1.5 bg-pink-50 text-pink-700 border border-pink-200 rounded-full px-2.5 py-1 text-xs font-medium"
          >
            <Mail className="w-3 h-3 opacity-70" />
            {email}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(idx);
                }}
                className="text-pink-500 hover:text-pink-700 transition-colors"
                aria-label={`Remove ${email}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={input}
          disabled={disabled}
          onChange={(e) => {
            setInput(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // If user typed an email but didn't press Enter, commit it on blur
            if (input.trim() && EMAIL_REGEX.test(input.trim())) {
              addEmail(input);
            }
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[140px] outline-none bg-transparent text-sm text-slate-800 placeholder:text-slate-400 disabled:cursor-not-allowed"
        />
      </div>

      {/* Suggestion dropdown */}
      {showDropdown && input.trim().length >= 2 && (
        <div className="relative">
          <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
            {searching ? (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Searching directory…
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">
                No matches. Press Enter to add "{input.trim()}" if it's a valid email.
              </div>
            ) : (
              suggestions.map((s, idx) => (
                <button
                  key={s.employeeId + s.email}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addEmail(s.email);
                  }}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3
                    ${highlightIndex === idx ? 'bg-pink-50' : 'hover:bg-slate-50'}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {(s.displayName || s.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{s.displayName}</div>
                    <div className="text-xs text-slate-500 truncate">{s.email}</div>
                  </div>
                  {s.jobTitle && (
                    <div className="text-xs text-slate-400 truncate max-w-[180px]">
                      {s.jobTitle}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
